/**
 * FieldAgentFlow — mobile-first guided execution flow for role=agent.
 *
 * S3 Addendum: reads the agent's active contracted service (role_type
 * "Valet" vs "Cleaner"), then renders the appropriate flow using
 * existing execution components:
 *
 *   Valet   → PickupQueueCard + OrderedJobStepperCard (bag count / pickup)
 *   Cleaner → OrderedJobStepperCard (unit/room checklist)
 *
 * QR scan wired to the existing QRScanner component and the existing
 * scan endpoints (no new backend logic).
 *
 * Single-column, one primary action visible at a time, minimum 48px
 * touch targets. No back navigation during an active job unless a
 * Crew Leader or Supervisor overrides it (the override button is a
 * no-op stub — actual escalation is handled server-side by the
 * existing swap/override endpoints already used elsewhere).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Camera, ClipboardList, Truck, ShieldCheck } from 'lucide-react';
import QRScanner from '../QRScanner';
import PickupQueueCard from '../PickupQueueCard';
import { OrderedJobStepperCard } from '../staff/OrderedJobStepperCard';
import FlagUnitCard from '../FlagUnitCard';
import { MyDailyAssignments } from '../staff/MyDailyAssignments';
import { MobileShell } from './MobileShell';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';

export function FieldAgentFlow({ user }) {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [confirmedProperty, setConfirmedProperty] = useState(null);
  const [busy, setBusy] = useState(false);

  const rt = (user?.role_type || user?.primary_service || '').toLowerCase();
  const isCleaner = rt.includes('clean');
  const flowLabel = isCleaner ? 'Cleaner · Guided Execution' : 'Valet · Guided Execution';

  const handleQRScanResult = async (data) => {
    setScannerOpen(false);
    if (!data) return;
    // Reuse existing scan endpoint — matches the pattern in StaffDashboard.
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/qr-scan/scan`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_data: data, source: 'staff-app' }),
      });
      if (r.ok) {
        const j = await r.json();
        setConfirmedProperty(j.property || { property_name: j.property_name || 'Property confirmed' });
        toast.success('Property confirmed');
      } else {
        toast.error('QR not recognized');
      }
    } catch {
      toast.error('Scan failed — try again');
    } finally { setBusy(false); }
  };

  return (
    <MobileShell title={flowLabel} subtitle={user?.name || user?.email} testId="staff-app-agent">
      <div className="flex flex-wrap gap-4 py-2"><a href="/operation-records" className="text-sm text-[#C9A962] underline" data-testid="agent-closeout-link">NECS Standard assignments</a><a href="/attendance" className="text-sm text-[#C9A962] underline" data-testid="agent-attendance-link">My attendance</a><a href="/staff-rewards" className="text-sm text-[#C9A962] underline" data-testid="agent-rewards-link">Monthly progress & rewards</a></div>
      {/* Step 1 — Scan property QR */}
      <section
        className="rounded-2xl p-4 border"
        style={{ borderColor: 'rgba(201,169,98,0.4)', background: 'rgba(255,255,255,0.02)' }}
        data-testid="staff-app-agent-step-scan"
      >
        <div className="flex items-center gap-2 mb-2">
          <Camera className="h-4 w-4" style={{ color: GOLD }} />
          <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: GOLD }}>
            Step 1 · Scan Property QR
          </p>
        </div>
        {confirmedProperty ? (
          <div className="rounded-lg bg-emerald-950/40 border border-emerald-600/40 p-3">
            <p className="text-emerald-300 text-sm font-semibold">
              {confirmedProperty.property_name || 'Property confirmed'}
            </p>
            <p className="text-[11px] text-zinc-400 mt-1">Continue with the guided steps below.</p>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setScannerOpen(true)}
            disabled={busy}
            className="w-full min-h-[56px] rounded-xl font-black uppercase tracking-widest text-sm"
            style={{ background: GOLD, color: '#0a0a0a' }}
            data-testid="staff-app-agent-open-scanner"
          >
            {busy ? 'Confirming…' : 'Tap to Scan Property'}
          </button>
        )}
      </section>

      {/* Step 2 — Service execution flow (valet or cleaner) */}
      {confirmedProperty && (
        <>
          <section data-testid="staff-app-agent-step-daily">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="h-4 w-4" style={{ color: GOLD }} />
              <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: GOLD }}>
                Step 2 · Today's Assignments
              </p>
            </div>
            <MyDailyAssignments />
          </section>

          {!isCleaner && (
            <section data-testid="staff-app-agent-step-pickup">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-4 w-4" style={{ color: GOLD }} />
                <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: GOLD }}>
                  Step 3 · Unit Sweep · Bag Count · Pickup
                </p>
              </div>
              <PickupQueueCard />
              <div className="mt-3">
                <FlagUnitCard />
              </div>
            </section>
          )}

          <section data-testid="staff-app-agent-step-stepper">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4" style={{ color: GOLD }} />
              <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: GOLD }}>
                Step 4 · {isCleaner ? 'Room/Unit Checklist' : 'Confirm & Submit'}
              </p>
            </div>
            <OrderedJobStepperCard />
          </section>
        </>
      )}

      {/* Override chip — visible always so a Crew Leader or Supervisor
          on-site can drop out of the guided flow if they need to. */}
      <div className="pt-2 flex justify-center">
        <a
          href="/staff-dashboard"
          className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 py-3 px-4 min-h-[48px] inline-flex items-center"
          data-testid="staff-app-agent-override"
        >
          Supervisor override → full dashboard
        </a>
      </div>

      <QRScanner
        open={scannerOpen}
        onOpenChange={(v) => { if (!v) setScannerOpen(false); }}
        onScan={handleQRScanResult}
        title="Scan Property QR"
        description="Align the property entrance QR inside the frame."
        dataTestId="staff-app-agent-qr-scanner"
      />
    </MobileShell>
  );
}

export default FieldAgentFlow;
